import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { usePrivateWorkspaceAgentSessionArtifactRows } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionArtifactRows';
import { usePrivateWorkspaceAgentSessionArtifactsList } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionArtifactsList';
import { usePrivateWorkspaceAgentTableArtifactRows } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentTableArtifactRows';
import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import {
  parseWorkspaceAgentTableRowsPage,
  type WorkspaceAgentTableRowsPage,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/workspaceAgentTableArtifact';
import {
  toWorkspaceAgentAssetListPillFromArtifactRows,
  toWorkspaceAgentBoardListPillFromArtifactRows,
  toWorkspaceAgentChangePreviewPillFromArtifact,
  toWorkspaceAgentTablePillFromArtifact,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentArtifactPillFromRows';
import {
  isWorkspaceAgentArtifactHydrationPill,
  workspaceAgentArtifactPillNeedsRowHydration,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentArtifactHydration';

export type UsePrivateWorkspaceAgentHydratedArtifactPillParams = {
  isStreaming: boolean;
  pill: WorkspaceAgentMessagePill;
};

export type PrivateWorkspaceAgentHydratedArtifactPillResult = {
  inViewRef: (node?: Element | null) => void;
  isPending: boolean;
  isUnavailable: boolean;
  pill: WorkspaceAgentMessagePill;
  /** Only set for `table` pills: the artifact can be longer than what has been loaded. */
  tablePaging?: {
    hasMore: boolean;
    isFetchNextPageError: boolean;
    isLoadingMore: boolean;
    loadMore: () => void;
    retryLoadMore: () => void;
  };
};

const EMPTY_ARTIFACT_IDS: readonly string[] = [];

export const usePrivateWorkspaceAgentHydratedArtifactPill = ({
  isStreaming,
  pill,
}: UsePrivateWorkspaceAgentHydratedArtifactPillParams): PrivateWorkspaceAgentHydratedArtifactPillResult => {
  const { sessionId, workspaceId } = usePrivateWorkspaceAgentPanelSession();
  const needsHydration =
    isWorkspaceAgentArtifactHydrationPill(pill) && workspaceAgentArtifactPillNeedsRowHydration(pill);
  const needsArtifact = needsHydration && (pill.type === 'changePreview' || pill.type === 'table');
  const { inView, ref } = useInView({ skip: !needsHydration, triggerOnce: true });
  const enabled = needsHydration && inView && Boolean(sessionId) && Boolean(workspaceId);

  const artifactsQuery = usePrivateWorkspaceAgentSessionArtifactsList(
    {
      artifactIds: needsArtifact ? [pill.id] : EMPTY_ARTIFACT_IDS,
      sessionId: sessionId ?? undefined,
      workspaceId: workspaceId ?? undefined,
    },
    { enabled: enabled && needsArtifact },
  );
  const isTable = pill.type === 'table';
  const rowsQuery = usePrivateWorkspaceAgentSessionArtifactRows(
    {
      artifactId: needsHydration && !isTable ? pill.id : undefined,
      sessionId: sessionId ?? undefined,
      workspaceId: workspaceId ?? undefined,
    },
    { enabled: enabled && !isTable },
  );
  const tableRowsQuery = usePrivateWorkspaceAgentTableArtifactRows(
    {
      artifactId: needsHydration && isTable ? pill.id : undefined,
      sessionId: sessionId ?? undefined,
      workspaceId: workspaceId ?? undefined,
    },
    { enabled: enabled && isTable },
  );

  const tablePages = tableRowsQuery.data?.pages;
  const tableRowsPage = useMemo((): WorkspaceAgentTableRowsPage | undefined => {
    const parsed = (tablePages ?? []).flatMap((page) => parseWorkspaceAgentTableRowsPage(page) ?? []);
    const last = parsed.at(-1);

    return last
      ? { data: parsed.flatMap((page) => page.data), pagination: { hasMore: last.pagination.hasMore }, type: 'table' }
      : undefined;
  }, [tablePages]);

  const artifact = artifactsQuery.data?.data.find((entry) => entry.id === pill.id);
  const rowsPage = rowsQuery.data;
  const rowsSuccess = isTable ? tableRowsQuery.data !== undefined : rowsQuery.isSuccess;
  const rowsError = isTable ? tableRowsQuery.isError && !tableRowsQuery.data : rowsQuery.isError;
  const { fetchNextPage, hasNextPage, isFetchNextPageError, isFetchingNextPage } = tableRowsQuery;
  const tablePaging = useMemo(
    () =>
      isTable
        ? {
            hasMore: hasNextPage,
            isFetchNextPageError,
            isLoadingMore: isFetchingNextPage,
            loadMore: () => {
              // fetchNextPage resolves (never rejects) without throwOnError; failures surface on query state.
              void fetchNextPage();
            },
            retryLoadMore: () => {
              void fetchNextPage();
            },
          }
        : undefined,
    [fetchNextPage, hasNextPage, isFetchNextPageError, isFetchingNextPage, isTable],
  );
  const artifactsReady = !needsArtifact || artifactsQuery.isSuccess;

  const hydrated = useMemo((): WorkspaceAgentMessagePill | undefined => {
    if (!needsHydration || !artifactsReady || !rowsSuccess) {
      return undefined;
    }

    if (pill.type === 'table') {
      return tableRowsPage
        ? toWorkspaceAgentTablePillFromArtifact({ artifact, pill, rowsPage: tableRowsPage })
        : undefined;
    }

    if (!rowsPage) {
      return undefined;
    }

    if (pill.type === 'assetList') {
      return toWorkspaceAgentAssetListPillFromArtifactRows({ artifact, pill, rowsPage });
    }

    if (pill.type === 'boardList') {
      return toWorkspaceAgentBoardListPillFromArtifactRows({ artifact, pill, rowsPage });
    }

    if (pill.type === 'changePreview') {
      return toWorkspaceAgentChangePreviewPillFromArtifact({ artifact, pill, rowsPage });
    }

    return pill;
  }, [artifact, artifactsReady, needsHydration, pill, rowsPage, rowsSuccess, tableRowsPage]);

  if (!needsHydration || hydrated) {
    return {
      inViewRef: ref,
      isPending: false,
      isUnavailable: false,
      pill: hydrated ?? pill,
      tablePaging,
    };
  }

  const hasFetched = artifactsReady && rowsSuccess;
  const hydrationFailed = rowsError || (needsArtifact && artifactsQuery.isError);
  const isPending = isStreaming || !inView || (!hasFetched && !hydrationFailed);

  return {
    inViewRef: ref,
    isPending,
    isUnavailable: !isPending,
    pill,
  };
};
